import moment from 'moment';
import * as XLSX from 'xlsx';
import {
  taskTypes,
  subTypes,
  urgencyTypes,
  statusTypes,
  DESCRIPTION_MAX_LENGTH,
} from '../configs/task.config';

export const generateCSVTemplate = () => {
  const headers = [
    'Task Name*',
    'Request Type*',
    'Request Sub Type',
    'Start Date (YYYY-MM-DD)',
    'Due Date* (YYYY-MM-DD)',
    'Urgency',
    'Channel Partner Name*',
    'Advertiser Name*',
    'Assignee Org Name',
    'Task Details',
    'Link URL',
  ];

  const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
  const dayAfterTomorrow = moment().add(2, 'days').format('YYYY-MM-DD');
  const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');

  const sampleRows = [
    [
      'Sample: Update Campaign Creative',
      'Display Campaign',
      'Update Creative(s)',
      tomorrow,
      nextWeek,
      'HIGH',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Please update the creative assets for Q4 campaign',
      'https://example.com/campaign',
    ],
    [
      'Sample: New Org Setup',
      'Onboarding',
      'Org Creation',
      tomorrow,
      dayAfterTomorrow,
      'MEDIUM',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Create new organization structure for client',
      '',
    ],
    [
      'Sample: SEO Analysis',
      'SEO',
      '',
      '',
      nextWeek,
      'LOW',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Perform SEO audit and provide recommendations',
      'https://example.com/seo-report',
    ],
  ];

  const csvLines = [
    headers.join(','),
    ...sampleRows.map(row => row.map(cell => {

      if (cell && /^\d{4}-\d{2}-\d{2}$/.test(cell)) {
        return `"'${cell}"`;
      }
      return `"${cell}"`;
    }).join(',')),
  ];

  const csvContent = csvLines.join('\n');

  return csvContent;
};

export const downloadCSVTemplate = () => {
  const headers = [
    'Task Name*',
    'Request Type*',
    'Request Sub Type',
    'Start Date (YYYY-MM-DD)',
    'Due Date* (YYYY-MM-DD)',
    'Urgency',
    'Channel Partner Name*',
    'Advertiser Name*',
    'Assignee Org Name',
    'Task Details',
    'Link URL',
  ];


  const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
  const dayAfterTomorrow = moment().add(2, 'days').format('YYYY-MM-DD');
  const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');

  const sampleRows = [
    [
      'Sample: Update Campaign Creative',
      'Display Campaign',
      'Update Creative(s)',
      tomorrow,
      nextWeek,
      'HIGH',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Please update the creative assets for Q4 campaign',
      'https://example.com/campaign',
    ],
    [
      'Sample: New Org Setup',
      'Onboarding',
      'Org Creation',
      tomorrow,
      dayAfterTomorrow,
      'MEDIUM',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Create new organization structure for client',
      '',
    ],
    [
      'Sample: SEO Analysis',
      'SEO',
      '',
      '',
      nextWeek,
      'LOW',
      'REPLACE_WITH_YOUR_CHANNEL_PARTNER_NAME',
      'REPLACE_WITH_YOUR_ADVERTISER_NAME',
      '',
      'Perform SEO audit and provide recommendations',
      'https://example.com/seo-report',
    ],
  ];

  const wsData = [headers, ...sampleRows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const columnWidths = [
    { wch: 30 }, // Task Name*
    { wch: 20 }, // Request Type*
    { wch: 20 }, // Request Sub Type
    { wch: 22 }, // Start Date
    { wch: 22 }, // Due Date
    { wch: 12 }, // Urgency
    { wch: 35 }, // Channel Partner Name*
    { wch: 35 }, // Advertiser Name*
    { wch: 25 }, // Assignee Org Name
    { wch: 50 }, // Task Details
    { wch: 40 }, // Link URL
  ];
  ws['!cols'] = columnWidths;

  // Format the date for prisma to accept
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let row = 1; row <= range.e.r; row++) { 
    const startDateCell = XLSX.utils.encode_cell({ r: row, c: 3 });
    if (ws[startDateCell]) {
      ws[startDateCell].t = 's'; 
      ws[startDateCell].z = '@'; 
    }

    const dueDateCell = XLSX.utils.encode_cell({ r: row, c: 4 });
    if (ws[dueDateCell]) {
      ws[dueDateCell].t = 's'; 
      ws[dueDateCell].z = '@'; 
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Task Import Template');

  XLSX.writeFile(wb, `bulk_task_import_template_${moment().format('YYYY-MM-DD')}.xlsx`);
};

export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const isExcel = file.name.endsWith('.xlsx');

    reader.onload = (e) => {
      try {
        let headers = [];
        let dataRows = [];

        if (isExcel) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

          if (jsonData.length < 2) {
            reject(new Error('Excel file is empty or contains only headers'));
            return;
          }

          headers = jsonData[0];
          dataRows = jsonData.slice(1);
        } else {
          const text = e.target.result;
          const lines = text.split('\n').filter((line) => {
            const trimmed = line.trim();
            return trimmed && !trimmed.startsWith('#');
          });

          if (lines.length < 2) {
            reject(new Error('CSV file is empty or contains only headers'));
            return;
          }

          headers = parseCSVLine(lines[0]);
          dataRows = lines.slice(1).map(line => parseCSVLine(line));
        }

        const rows = [];
        dataRows.forEach((values, index) => {
          if (values && values.some((val) => String(val).trim())) {
            const rowData = {};
            headers.forEach((header, headerIndex) => {
              let cellValue = String(values[headerIndex] || '').trim();

              if (header.includes('Date') && cellValue && /^\d+$/.test(cellValue)) {
                cellValue = convertExcelSerialDate(cellValue);
              }

              rowData[header] = cellValue;
            });
            rows.push({ lineNumber: index + 2, data: rowData });
          }
        });

        if (rows.length === 0) {
          reject(new Error('File contains no valid data rows'));
          return;
        }

        resolve(rows);
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

// Helper function to convert Excel serial date to YYYY-MM-DD format
const convertExcelSerialDate = (serial) => {
  const num = Number(serial);
  if (isNaN(num) || num < 1 || num > 100000) {
    return serial;
  }

  const excelEpoch = new Date(1900, 0, 1);
  const days = num > 59 ? num - 2 : num - 1; // Account for Excel's 1900 leap year bug
  const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);

  return moment(date).format('YYYY-MM-DD');
};

const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result.map((val) => {
    const trimmed = val.trim();
    return trimmed.startsWith("'") ? trimmed.substring(1) : trimmed;
  });
};

export const validateTaskRow = (rowData, lineNumber) => {
  const errors = [];
  const warnings = [];

  const taskName = rowData['Task Name*']?.trim();
  if (!taskName) {
    errors.push(`❌ Row ${lineNumber}: Task Name is required (cannot be empty)`);
  } else if (taskName.length > 255) {
    errors.push(`❌ Row ${lineNumber}: Task Name is too long (${taskName.length} characters). Maximum allowed: 255 characters`);
  }

  const requestType = rowData['Request Type*']?.trim();
  if (!requestType) {
    errors.push(`❌ Row ${lineNumber}: Request Type is required. Valid values: ${taskTypes.slice(0, 3).join(', ')}, ... (see field guide for all ${taskTypes.length} types)`);
  } else if (!taskTypes.includes(requestType)) {
    errors.push(
      `❌ Row ${lineNumber}: Invalid Request Type "${requestType}". This value doesn't match any valid type. Valid values are: ${taskTypes.join(', ')}`
    );
  }

  const requestSubType = rowData['Request Sub Type']?.trim();
  if (requestSubType && requestType) {
    const validSubTypes = subTypes[requestType] || [];
    if (validSubTypes.length > 0 && !validSubTypes.includes(requestSubType)) {
      warnings.push(
        `⚠️ Row ${lineNumber}: Request Sub Type "${requestSubType}" is not a standard option for "${requestType}". Valid options: ${validSubTypes.join(', ')}`
      );
    }
  }

  const startDateStr = rowData['Start Date (YYYY-MM-DD)']?.trim();
  if (startDateStr) {
    const startDate = moment(startDateStr, 'YYYY-MM-DD', true);
    if (!startDate.isValid()) {
      errors.push(`❌ Row ${lineNumber}: Invalid Start Date "${startDateStr}". Must use format YYYY-MM-DD (e.g., 2025-12-31)`);
    }
  }

  const dueDateStr = rowData['Due Date* (YYYY-MM-DD)']?.trim();
  if (!dueDateStr) {
    errors.push(`❌ Row ${lineNumber}: Due Date is required (cannot be empty)`);
  } else {
    const dueDate = moment(dueDateStr, 'YYYY-MM-DD', true);
    if (!dueDate.isValid()) {
      errors.push(`❌ Row ${lineNumber}: Invalid Due Date "${dueDateStr}". Must use format YYYY-MM-DD (e.g., 2025-12-31)`);
    } else if (startDateStr) {
      const startDate = moment(startDateStr, 'YYYY-MM-DD', true);
      if (startDate.isValid() && dueDate.isBefore(startDate)) {
        errors.push(`❌ Row ${lineNumber}: Due Date (${dueDateStr}) cannot be before Start Date (${startDateStr})`);
      }
    }
  }

  const urgency = rowData['Urgency']?.trim().toUpperCase();
  if (urgency) {
    const validUrgencies = Object.values(urgencyTypes);
    if (!validUrgencies.includes(urgency)) {
      errors.push(
        `❌ Row ${lineNumber}: Invalid Urgency "${urgency}". Valid values: ${validUrgencies.join(', ')}`
      );
    }
  }

  const channelPartner = rowData['Channel Partner Name*']?.trim();
  if (!channelPartner) {
    errors.push(`❌ Row ${lineNumber}: Channel Partner Name is required. Must exactly match an existing organization name (case-sensitive)`);
  }

  const advertiser = rowData['Advertiser Name*']?.trim();
  if (!advertiser) {
    errors.push(`❌ Row ${lineNumber}: Advertiser Name is required. Must exactly match an existing advertiser name (case-sensitive)`);
  }

  const taskDetails = rowData['Task Details']?.trim();
  if (taskDetails && taskDetails.length > DESCRIPTION_MAX_LENGTH) {
    errors.push(`❌ Row ${lineNumber}: Task Details is too long (${taskDetails.length} characters). Maximum allowed: ${DESCRIPTION_MAX_LENGTH} characters`);
  }

  const linkUrl = rowData['Link URL']?.trim();
  if (linkUrl && linkUrl.length > 499) {
    errors.push(`❌ Row ${lineNumber}: Link URL is too long (${linkUrl.length} characters). Maximum allowed: 499 characters`);
  }

  return { errors, warnings };
};

export const validateAllRows = (rows) => {
  const allErrors = [];
  const allWarnings = [];

  rows.forEach((row) => {
    const { errors, warnings } = validateTaskRow(row.data, row.lineNumber);
    allErrors.push(...errors);
    allWarnings.push(...warnings);
  });

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

export const buildTaskCreateData = (rowData, currentUser, orgMappings) => {
  const {
    channelPartnerOrg,
    advertiserOrg,
    assigneeOrg,
  } = orgMappings;

  const taskName = rowData['Task Name*']?.trim();
  const requestType = rowData['Request Type*']?.trim();
  const requestSubType = rowData['Request Sub Type']?.trim() || null;
  const startDateStr = rowData['Start Date (YYYY-MM-DD)']?.trim();
  const dueDateStr = rowData['Due Date* (YYYY-MM-DD)']?.trim();
  const urgency = rowData['Urgency']?.trim().toUpperCase() || null;
  const taskDetails = rowData['Task Details']?.trim() || null;
  const linkUrl = rowData['Link URL']?.trim() || null;

  const data = {
    name: taskName,
    requestType,
    requestSubType,
    startDate: startDateStr ? moment(startDateStr, 'YYYY-MM-DD').toISOString() : null,
    dueDate: dueDateStr ? moment(dueDateStr, 'YYYY-MM-DD').toISOString() : null,
    urgency,
    taskDetails,
    linkUrl,
    status: 'NOT_STARTED',
    createdBy: {
      connect: {
        id: currentUser.id,
      },
    },
    assigneeUsers: {}, 
  };

  if (channelPartnerOrg) {
    data.channelPartner = {
      connect: {
        id: channelPartnerOrg.id,
      },
    };
  }

  if (advertiserOrg) {
    data.advertiser = {
      connect: {
        id: advertiserOrg.id,
      },
    };
  }

  if (assigneeOrg) {
    data.assigneeOrg = {
      connect: {
        id: assigneeOrg.id,
      },
    };
  }

  return data;
};
